import React, { useEffect, useMemo, useState } from 'react';

type Practice = {
    id: number;
    name: string;
    address: string;
    pin: string;
    phone?: string;
    description?: string;
};

type ApiResponse = {
    success: boolean;
    data?: Practice[];
    error?: { code: string; message: string };
};

interface PracticeSelectorProps {
    onPracticeSelect: (practice: Practice) => void;
}

const PracticeSelector: React.FC<PracticeSelectorProps> = ({ onPracticeSelect }) => {
    const [practices, setPractices] = useState<Practice[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState<string>("");

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();

        async function fetchPractices() {
            try {
                setLoading(true);
                setError(null);
                const res = await fetch('/api/practices.php', {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                    signal: controller.signal
                });

                if (!res.ok) {
                    throw new Error(`Request failed (${res.status})`);
                }
                const json: ApiResponse = await res.json();
                if (!json.success) {
                    throw new Error(json.error?.message || 'Failed to load practices');
                }

                if (!cancelled) {
                    setPractices(Array.isArray(json.data) ? json.data : []);
                }
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || 'Unable to load practices');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchPractices();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return practices;
        return practices.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.address || '').toLowerCase().includes(q) ||
            (p.pin || '').toLowerCase().includes(q)
        );
    }, [practices, query]);

    if (loading) {
        return (
            <div className="text-center p-8">
                <div className="text-lg">Loading practice locations...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-8">
                <div className="text-red-600 mb-4">{error}</div>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto">
            <div className="text-center mb-6">
                <img
                    src="/Public/images/envision_logo_with_text.png"
                    alt="EnvisionEHR Logo"
                    className="mx-auto mb-3 h-14 w-auto"
                />
                <h2 className="text-2xl font-bold">Select Your Practice</h2>
            </div>

            <div className="mb-4">
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search by name, city, or PIN"
                    className="w-full px-3 py-2 border rounded-md"
                />
            </div>

            <div className="space-y-3">
                {filtered.map(practice => (
                    <button
                        key={practice.id}
                        onClick={() => onPracticeSelect(practice)}
                        className="w-full p-4 text-left border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                        <div className="font-medium text-lg">{practice.name}</div>
                        <div className="text-sm text-gray-600 mt-1">{practice.address}</div>
                        {practice.description && (
                            <div className="text-sm text-gray-500 mt-1">{practice.description}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">PIN: {practice.pin}</div>
                    </button>
                ))}
                {filtered.length === 0 && (
                    <div className="text-center text-gray-500 py-8">No practices match your search.</div>
                )}
            </div>
        </div>
    );
};

export default PracticeSelector;

